import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export interface TwoFactorStatus {
  hasMFA: boolean;
  isEnrolled: boolean;
  loading: boolean;
}

export function use2FA() {
  const { user } = useAuth();
  const [status, setStatus] = useState<TwoFactorStatus>({
    hasMFA: false,
    isEnrolled: false,
    loading: true
  });

  useEffect(() => {
    checkMFAStatus();
  }, [user?.id]);

  const checkMFAStatus = async () => {
    if (!user) {
      setStatus({ hasMFA: false, isEnrolled: false, loading: false });
      return;
    }

    try {
      // Check if user has any MFA factors
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) throw factorsError;

      const hasTOTP = factors?.totp && factors.totp.length > 0;
      const isVerified = factors?.totp?.some(f => f.status === 'verified') || false;

      // Check database status
      const { data: dbStatus, error: dbError } = await supabase
        .from('admin_2fa_status')
        .select('mfa_enabled')
        .eq('user_id', user.id)
        .single();

      if (dbError && dbError.code !== 'PGRST116') {
        console.error('Error checking 2FA status:', dbError);
      }

      setStatus({
        hasMFA: hasTOTP && isVerified,
        isEnrolled: dbStatus?.mfa_enabled || false,
        loading: false
      });
    } catch (error) {
      console.error('Error checking MFA status:', error);
      setStatus({ hasMFA: false, isEnrolled: false, loading: false });
    }
  };

  const enroll2FA = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });

      if (error) throw error;

      return {
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        factorId: data.id
      };
    } catch (error: any) {
      console.error('Error enrolling 2FA:', error);
      toast.error('Erreur lors de l\'activation du 2FA: ' + error.message);
      throw error;
    }
  };

  const verify2FA = async (factorId: string, code: string) => {
    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code
      });

      if (error) throw error;

      // Update database status
      const { error: updateError } = await supabase.rpc('update_admin_2fa_status', {
        p_user_id: user!.id,
        p_enabled: true
      });

      if (updateError) {
        console.error('Error updating 2FA status:', updateError);
      }

      await checkMFAStatus();
      toast.success('Authentification à deux facteurs activée avec succès !');
      
      return true;
    } catch (error: any) {
      console.error('Error verifying 2FA:', error);
      toast.error('Code invalide. Veuillez réessayer.');
      return false;
    }
  };

  const disable2FA = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      
      if (factors?.totp && factors.totp.length > 0) {
        for (const factor of factors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Update database status
      const { error: updateError } = await supabase.rpc('update_admin_2fa_status', {
        p_user_id: user!.id,
        p_enabled: false
      });

      if (updateError) {
        console.error('Error updating 2FA status:', updateError);
      }

      await checkMFAStatus();
      toast.success('Authentification à deux facteurs désactivée');
      
      return true;
    } catch (error: any) {
      console.error('Error disabling 2FA:', error);
      toast.error('Erreur lors de la désactivation du 2FA');
      return false;
    }
  };

  return {
    status,
    enroll2FA,
    verify2FA,
    disable2FA,
    refresh: checkMFAStatus
  };
}
